/**
 * applyDelta.ts
 *
 * Client-side application of the `dataDelta` Socket.IO payloads emitted by the
 * backend MqttPipeline. The server sends only the leaves that changed (each
 * patch carries the FULL current value at that leaf), plus a per-org monotonic
 * `seq` for gap detection and an occasional `reset` snapshot.
 *
 * The apply uses STRUCTURAL SHARING: every object along a changed path is
 * shallow-cloned, while untouched sibling branches keep their existing
 * references. That lets React skip re-rendering subtrees whose data did not
 * change (ref-equality short-circuits), which is the core of the client-side
 * performance fix.
 *
 * NOTE: the client never merges at the leaf. The server already merged and
 * sends the authoritative value, so we simply replace the leaf. This avoids the
 * fragile text-diff approach that previously caused silent data loss.
 */

export interface OrgDeltaPatch {
  /** Path = topic split on "/". p[0] is the org name. */
  p: string[];
  /** New full value at the leaf. Omitted when `del` is set. */
  v?: unknown;
  /** When true, delete the leaf at `p`. */
  del?: boolean;
}

export interface OrgDeltaPayload {
  orgId: string;
  seq: number;
  reset?: boolean;
  patches: OrgDeltaPatch[];
}

type Dict = Record<string, any>;

function isPlainObject(v: unknown): v is Dict {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Return a new root with `value` set (or deleted) at `path`, sharing structure
 * with `root` for every branch that did not change. `path[0]` is the org key.
 *
 * Mirrors the server's mergeInto intermediate semantics: a non-plain-object
 * node encountered along the path is replaced with a fresh object.
 */
export function setPath(root: Dict, path: string[], value: unknown, del = false): Dict {
  if (path.length === 0) return root;

  // For a delete whose parent chain does not exist there is nothing to remove;
  // return the original reference so callers can skip the state update.
  if (del) {
    let probe: unknown = root;
    for (let i = 0; i < path.length - 1; i++) {
      if (!isPlainObject(probe)) return root;
      probe = probe[path[i]];
    }
    if (!isPlainObject(probe) || !(path[path.length - 1] in probe)) return root;
  }

  const newRoot: Dict = { ...root };
  let cur: Dict = newRoot;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    cur[key] = isPlainObject(cur[key]) ? { ...cur[key] } : {};
    cur = cur[key];
  }

  const leafKey = path[path.length - 1];
  if (del) {
    delete cur[leafKey];
  } else {
    cur[leafKey] = value;
  }
  return newRoot;
}

/**
 * Apply every patch in a delta payload to `root`, returning a new root (with
 * structural sharing). Works for both incremental and `reset` payloads — a
 * reset is simply a single root-level patch `{ p: [orgId], v: fullState }`.
 *
 * Returns the SAME reference when no patch changed anything (e.g. a delete for
 * a path that does not exist), so callers can avoid a needless re-render.
 */
export function applyDelta(root: Dict, payload: OrgDeltaPayload): Dict {
  let next = root;
  for (const patch of payload.patches) {
    if (!patch || !Array.isArray(patch.p) || patch.p.length === 0) continue;
    next = setPath(next, patch.p, patch.v, patch.del === true);
  }
  return next;
}
